import { z } from "zod";

export const InboxValues = [
  "my-inbox",
  "my-urgent",
  "my-snoozed",
  "my-tickets",
  "my-triage",
  "my-conversations",
  "my-tasks",
  "my-escalations",
  "my-created",
  "unassigned-conversations",
  "unassigned-tickets",
  "open-tickets",
  "triage-tickets",
  "open-conversations",
  "active-escalations",
  "triage-escalations",
  "unassigned-escalations",
  "open-tasks",
  "unassigned-tasks",
  "urgent-tickets",
  "snoozed",
  "custom",
] as const;
export const InboxSchema = z.enum(InboxValues);
export type Inbox = z.infer<typeof InboxSchema>;
export const Inbox = InboxSchema.enum;

type InboxScope = "PERSON" | "TEAM" | "CUSTOM";

// Total over Inbox so a queue added later cannot compile until it is classified. A runtime pinning
// test would let it ship scoped nowhere, which renders it in no section at all.
export const INBOX_SCOPE = {
  "my-inbox": "PERSON",
  "my-urgent": "PERSON",
  "my-snoozed": "PERSON",
  "my-tickets": "PERSON",
  "my-triage": "PERSON",
  "my-conversations": "PERSON",
  "my-tasks": "PERSON",
  "my-escalations": "PERSON",
  "my-created": "PERSON",
  "unassigned-conversations": "TEAM",
  "unassigned-tickets": "TEAM",
  "open-tickets": "TEAM",
  "triage-tickets": "TEAM",
  "open-conversations": "TEAM",
  "active-escalations": "TEAM",
  "unassigned-escalations": "TEAM",
  "triage-escalations": "TEAM",
  "open-tasks": "TEAM",
  "unassigned-tasks": "TEAM",
  "urgent-tickets": "TEAM",
  snoozed: "TEAM",
  custom: "CUSTOM",
} as const satisfies Record<Inbox, InboxScope>;

export type TeamInbox = keyof { [K in Inbox as (typeof INBOX_SCOPE)[K] extends "TEAM" ? K : never]: true };

export const InboxDisplayName: Record<Inbox, string> = {
  "my-inbox": "My inbox",
  "my-urgent": "My urgent",
  "my-snoozed": "My snoozed",
  "my-created": "My created",
  "my-tickets": "My tickets",
  "my-triage": "My triage",
  "my-conversations": "My conversations",
  "my-tasks": "My tasks",
  "my-escalations": "My escalations",
  "unassigned-tickets": "Unassigned tickets",
  "unassigned-conversations": "Unassigned conversations",
  "open-tickets": "Open tickets",
  "triage-tickets": "Triage tickets",
  "open-conversations": "Open conversations",
  "active-escalations": "Active escalations",
  "triage-escalations": "Triage escalations",
  "unassigned-escalations": "Unassigned escalations",
  "open-tasks": "Open tasks",
  "unassigned-tasks": "Unassigned tasks",
  "urgent-tickets": "Urgent tickets",
  snoozed: "Snoozed",
  custom: "Custom",
};

// Resolved server-side to a bound Date before the task arm compiles. A client-computed instant would
// instead fragment the inbox count cache key every request and freeze a timestamp into saved views.
export const TASK_DUE_DATE_BOUNDARY_TOKEN = "TASK_DUE_DATE_BOUNDARY";

// 13 digits (~316 years of ms) bounds Number() well inside safe-integer range; the cap keeps every
// resolvable instant inside MySQL DATETIME range.
const RELATIVE_DATE_TOKEN_REGEX = /^NOW([+-])(\d{1,13})$/;
export const MAX_RELATIVE_DATE_OFFSET_MS = 100 * 365 * 24 * 60 * 60 * 1000;

// Resolved instants snap to this boundary so Redis-cached counts (TTL <= 180s) and the live list land
// on the same instant within a bucket, and results stay stable while a user paginates.
export const RELATIVE_DATE_SNAP_MS = 600_000;

// Deliberately excludes bare NOW — that stays the MySqlValue sentinel (compiled to literal NOW()).
export const isRelativeDateTokenCandidate = (value: string): boolean =>
  value.startsWith("NOW+") || value.startsWith("NOW-");

export const parseRelativeDateToken = (value: string): { offsetMs: number } | null => {
  const match = RELATIVE_DATE_TOKEN_REGEX.exec(value);
  if (!match) {
    return null;
  }
  const [, sign, digits] = match;
  const magnitude = Number(digits);
  if (magnitude > MAX_RELATIVE_DATE_OFFSET_MS) {
    return null;
  }
  if (magnitude === 0) {
    return { offsetMs: 0 };
  }
  return { offsetMs: sign === "-" ? -magnitude : magnitude };
};

export const buildRelativeDateToken = (offsetMs: number): string => {
  const magnitude = Math.abs(Math.trunc(offsetMs));
  return `NOW${offsetMs < 0 ? "-" : "+"}${magnitude}`;
};

export const resolveRelativeDateToken = (value: string, now: Date): Date | null => {
  const parsed = parseRelativeDateToken(value);
  if (!parsed) {
    return null;
  }
  const snapped = Math.floor((now.getTime() + parsed.offsetMs) / RELATIVE_DATE_SNAP_MS) * RELATIVE_DATE_SNAP_MS;
  return new Date(snapped);
};

// Filter fields with no column behind them: the inbox query strips each from the definition and applies
// it as a correlated predicate, so the producer and the SQL layer must agree on the exact name.
export const InboxPredicateField = {
  bidId: "bidId",
  awaitingReply: "awaitingReply",
  lastTouchedBefore: "lastTouchedBefore",
} as const;
